'use client';

import { useCallback, useEffect, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';

/**
 * One element to nudge on pointer move. `strength` is the fraction of the
 * cursor-to-centre vector that the element translates by (e.g. 0.15 =
 * 15% of the distance).
 */
export interface MagneticTarget {
  ref: RefObject<HTMLElement | null>;
  strength: number;
}

interface MagneticHandlers {
  onMouseEnter: () => void;
  onMouseMove: (event: ReactMouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
}

/**
 * Magnetic-hover hook.
 *
 * Avoids the classic forced-reflow pattern by:
 *   1. Measuring the primary target's DOMRect ONCE on `mouseenter` and
 *      caching it for the lifetime of the hover. Every subsequent
 *      `mousemove` is a pure style write — no layout read — so the browser
 *      never has to synchronously flush pending styles to answer the
 *      call.
 *   2. Coalescing style writes in `requestAnimationFrame`. Multiple
 *      mousemoves within one frame collapse to a single transform update,
 *      which further reduces thrash on high-polling-rate mice.
 *   3. Caching `prefers-reduced-motion` via a MediaQueryList change
 *      listener. `matchMedia()` is *not* called on every mousemove.
 *   4. Re-measuring only on `scroll` or `resize` while hovered. If the
 *      pointer is not inside the element we skip the layout read entirely.
 *
 * Pass one target for single-element magnetic hover, or two (outer +
 * inner) for a nested pull effect like the Circle CTA.
 */
export function useMagneticHover(targets: MagneticTarget[]): MagneticHandlers {
  // Mirror `targets` into a ref so the event callbacks can read the latest
  // refs + strengths without adding them as dependencies (which would
  // recreate the callbacks every render and break onMouseMove stability).
  const targetsRef = useRef(targets);
  targetsRef.current = targets;

  const rectRef = useRef<DOMRect | null>(null);
  const reducedMotionRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const pendingRef = useRef<{ clientX: number; clientY: number } | null>(null);

  // Cache prefers-reduced-motion once + subscribe to live changes.
  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotionRef.current = mql.matches;
    const handleChange = (event: MediaQueryListEvent): void => {
      reducedMotionRef.current = event.matches;
    };
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // Re-measure the cached rect when the viewport changes mid-hover so the
  // cursor math doesn't drift after a scroll/resize.
  useEffect(() => {
    const invalidate = (): void => {
      if (rectRef.current === null) return;
      const primary = targetsRef.current[0]?.ref.current;
      if (primary) rectRef.current = primary.getBoundingClientRect();
    };
    window.addEventListener('scroll', invalidate, { passive: true });
    window.addEventListener('resize', invalidate);
    return () => {
      window.removeEventListener('scroll', invalidate);
      window.removeEventListener('resize', invalidate);
    };
  }, []);

  // Cancel any pending rAF on unmount to avoid setting transforms on
  // nodes that React has already detached.
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const onMouseEnter = useCallback((): void => {
    if (reducedMotionRef.current) return;
    const primary = targetsRef.current[0]?.ref.current;
    if (primary) rectRef.current = primary.getBoundingClientRect();
  }, []);

  const commit = useCallback((): void => {
    rafRef.current = null;
    const pending = pendingRef.current;
    const rect = rectRef.current;
    if (!pending || !rect) return;

    const dx = pending.clientX - (rect.left + rect.width / 2);
    const dy = pending.clientY - (rect.top + rect.height / 2);

    for (const { ref, strength } of targetsRef.current) {
      const el = ref.current;
      if (!el) continue;
      el.style.transform = `translate(${dx * strength}px, ${dy * strength}px)`;
    }
  }, []);

  const onMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLElement>): void => {
      if (reducedMotionRef.current || rectRef.current === null) return;
      pendingRef.current = {
        clientX: event.clientX,
        clientY: event.clientY,
      };
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(commit);
    },
    [commit],
  );

  const onMouseLeave = useCallback((): void => {
    rectRef.current = null;
    pendingRef.current = null;
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    for (const { ref } of targetsRef.current) {
      const el = ref.current;
      if (el) el.style.transform = 'translate(0, 0)';
    }
  }, []);

  return { onMouseEnter, onMouseMove, onMouseLeave };
}
